<script setup>
import { computed, onBeforeUnmount } from 'vue';
import { storeToRefs } from 'pinia';
import { useRouter } from 'vue-router';
import { useScreensizeStore } from '@stores/useScreensizeStore';
import { trans } from '@elements/translation';
import { useDataTableStore } from '@stores/useDataTableStore';
import { PAY_SUBSCRIPTION_ENDPOINT } from '@tools/routes/routes';
import useAccountStore from '../store/account';
import StripeSoonLimited from '../Onboarding/StripeSoonLimited';
import { FRONT_STRIPE_STATUS, PENDING_STRIPE, STRIPE_LIMITED, STRIPE_STATUS } from '../constants/constants';
import SubscriptionDataList from './SubscriptionDataList.vue';
import SubscriptionTable from './SubscriptionDataTable.vue';
import { EVENT_SLIDERS } from '../general/event-constants';
import useInvoiceModalStore from '../store/invoiceModal';
import useManagePayAccount from '../store/managePayAccount';

const accountStore = useAccountStore();
const { isMobile } = storeToRefs(useScreensizeStore());
const { setModal } = useInvoiceModalStore();

accountStore.setPayOrganizationSeller(window.initialState.relatedOrganization);
accountStore.setStripeAccountStatus(
  window.initialState.relatedOrganization?.account_status ?? STRIPE_STATUS.NOT_STARTED,
);

const router = useRouter();
const { payOrganizationSeller, stripeAccountStatus } = storeToRefs(accountStore);

const managePayAccount = useManagePayAccount();
managePayAccount.setBillingMode(
  window.initialState.organizationType,
  window.initialState.billingType,
);
const datatableStore = useDataTableStore();

datatableStore.setEndpoint(PAY_SUBSCRIPTION_ENDPOINT);
datatableStore.setDefaultOrder('subscription.id', 'desc');

const createStripeAccount = () => {
  setModal({
    modalTitle: trans('gestion.subscription.slider.create_account.title'),
    modalComponentName: EVENT_SLIDERS.CREATE_STRIPE_ACCOUNT,
    shouldOpenModal: true,
  });
};

const canCreateNewSubscription = computed(() => {
  if (!payOrganizationSeller.value) {
    return false;
  }

  if (!payOrganizationSeller.value.payment_method_id || !payOrganizationSeller.value.has_kyc_active) {
    return false;
  }

  return payOrganizationSeller.value.account_status !== FRONT_STRIPE_STATUS.READY
    || !payOrganizationSeller.value.client_invoice_data;
});

const createSubscription = () => {
  if (!canCreateNewSubscription.value) {
    createStripeAccount();
    return;
  }
  router.push({ name: 'subscriptionsCreate' });
};

onBeforeUnmount(() => datatableStore.reset());
</script>

<template>
  <div class="subscriptions">
    <div class="subscriptions__content">
      <div class="subscriptions__header">
        <div class="subscriptions__header-title">
          {{ trans('gestion.subscription.title') }}
        </div>
        <div class="subscriptions__header-buttons">
          <button
            class="btn btn--primary btn--small subscriptions__btn"
            :class="stripeAccountStatus ? `subscriptions__btn--${stripeAccountStatus}` : ''"
            :disabled="PENDING_STRIPE.includes(accountStore.stripeAccountStatus)"
            @click="createSubscription"
          >
            {{ trans('messaging.decision.stripe.subscription.create') }}
          </button>
        </div>
      </div>
      <StripeSoonLimited v-if="STRIPE_LIMITED.includes(accountStore.stripeAccountStatus)" />

      <SubscriptionDataList v-if="isMobile" @create-subscription="createSubscription" />
      <SubscriptionTable v-else @create-subscription="createSubscription" />
    </div>
  </div>
</template>
